import { Check, Loader2, Plus, Tag, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { tagService } from "@/lib/services";
import type { EntityTagResponse, TaggableEntityType, TagSummary } from "@/types";

export interface TagPopoverProps {
  entityType: TaggableEntityType;
  entityId: string;
  /** Compact mode renders just a tag icon button; full mode renders inline tag chips */
  mode?: "compact" | "full";
}

/**
 * TagPopover — a reusable component for viewing and managing tags on any entity.
 *
 * Renders current tags as chips and provides a popover to search/add/remove tags.
 */
export function TagPopover({ entityType, entityId, mode = "full" }: Readonly<TagPopoverProps>) {
  const [entityTags, setEntityTags] = useState<EntityTagResponse[]>([]);
  const [allTags, setAllTags] = useState<TagSummary[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [applying, setApplying] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Load entity tags
  const loadEntityTags = useCallback(async () => {
    try {
      const res = await tagService.listEntityTags(entityType, entityId);
      setEntityTags(res.data);
    } catch (err) {
      console.error("Failed to load entity tags:", err);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    loadEntityTags();
  }, [loadEntityTags]);

  // Load all available tags when popover opens
  const loadAllTags = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await tagService.list({ pageSize: 100 });
      setAllTags(res.data);
    } catch (err) {
      console.error("Failed to load tags:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadAllTags();
      setSearch("");
    }
  }, [isOpen, loadAllTags]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  const appliedTagUuids = new Set(entityTags.map((et) => et.tagUuid));

  const filteredTags = allTags.filter((tag) => {
    if (search) {
      return tag.name.toLowerCase().includes(search.toLowerCase());
    }
    return true;
  });

  const handleApply = async (tagUuid: string) => {
    try {
      setApplying(tagUuid);
      await tagService.applyTag({
        tagUuid,
        entityType,
        entityId,
      });
      await loadEntityTags();
    } catch (err) {
      console.error("Failed to apply tag:", err);
    } finally {
      setApplying(null);
    }
  };

  const handleRemove = async (tagUuid: string) => {
    try {
      setRemoving(tagUuid);
      await tagService.removeTag({
        tagUuid,
        entityType,
        entityId,
      });
      setEntityTags((prev) => prev.filter((et) => et.tagUuid !== tagUuid));
    } catch (err) {
      console.error("Failed to remove tag:", err);
    } finally {
      setRemoving(null);
    }
  };

  const toggleTag = async (tagUuid: string) => {
    if (appliedTagUuids.has(tagUuid)) {
      await handleRemove(tagUuid);
    } else {
      await handleApply(tagUuid);
    }
  };

  return (
    <div className="relative inline-flex items-center gap-1.5">
      {/* Inline tag chips (full mode only) */}
      {mode === "full" && entityTags.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap">
          {entityTags.map((et) => (
            <span
              key={et.tagUuid}
              className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full border"
              style={{
                borderColor: `${et.tagColor}40`,
                color: et.tagColor,
              }}
            >
              {et.tagName}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(et.tagUuid);
                }}
                className="hover:opacity-70 transition"
              >
                {removing === et.tagUuid ? (
                  <Loader2 className="w-2.5 h-2.5 animate-spin" />
                ) : (
                  <X className="w-2.5 h-2.5" />
                )}
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Trigger button */}
      <button
        ref={triggerRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        className={`flex items-center gap-1 transition ${
          mode === "compact"
            ? "p-1.5 hover:bg-interactive-hover rounded-lg text-content-tertiary hover:text-content-primary"
            : "p-1 hover:bg-interactive-hover rounded-sm text-content-quaternary hover:text-content-tertiary"
        }`}
        title="Manage tags"
      >
        <Tag className={mode === "compact" ? "w-4 h-4" : "w-3.5 h-3.5"} />
        {mode === "compact" && entityTags.length > 0 && <span className="text-xs">{entityTags.length}</span>}
      </button>

      {/* Popover */}
      {isOpen && (
        <div
          ref={popoverRef}
          className="absolute top-full right-0 mt-1 w-64 bg-surface-primary border border-border-primary rounded-lg shadow-xl z-50"
          role="presentation" // NOSONAR — decorative backdrop element
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search */}
          <div className="p-2 border-b border-border-primary">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tags..."
              className="w-full px-2.5 py-1.5 bg-surface-secondary border border-border-secondary rounded-sm text-sm text-content-primary placeholder-content-quaternary focus:outline-hidden focus:ring-1 focus:ring-brand-primary"
              autoFocus
            />
          </div>

          {/* Tag list */}
          <div className="max-h-48 overflow-y-auto p-1">
            {(() => {
              if (isLoading) {
                return (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-4 h-4 text-brand-primary animate-spin" />
                  </div>
                );
              }
              if (filteredTags.length === 0) {
                return (
                  <p className="text-xs text-content-quaternary text-center py-3">
                    {search ? "No matching tags" : "No tags available"}
                  </p>
                );
              }
              return filteredTags.map((tag) => {
                const isApplied = appliedTagUuids.has(tag.uuid);
                const isBusy = applying === tag.uuid || removing === tag.uuid;
                return (
                  <button
                    key={tag.uuid}
                    onClick={() => toggleTag(tag.uuid)}
                    disabled={isBusy}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-sm text-left text-sm hover:bg-interactive-hover transition disabled:opacity-50"
                  >
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: tag.color }} />
                    <span className="flex-1 truncate text-content-primary">{tag.name}</span>
                    {(() => {
                      if (isBusy)
                        return <Loader2 className="w-3.5 h-3.5 text-content-tertiary animate-spin shrink-0" />;
                      if (isApplied) return <Check className="w-3.5 h-3.5 text-green-400 shrink-0" />;
                      return <Plus className="w-3.5 h-3.5 text-content-quaternary shrink-0" />;
                    })()}
                  </button>
                );
              });
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
